// file: client/src/writer/utils/viewEditUtils.ts

import React, { useState } from 'react';
import { actionButtonClass, primaryButtonClass, secondaryButtonClass } from '../constants';

/**
 * Formats display values for view mode with consistent styling
 * Shows "Not specified" for empty values and handles long text truncation
 */
export const formatDisplayValue = (value: string | number | undefined, maxLength: number = 200) => {
    if (!value) return <span className="text-gray-500 italic">Not specified</span>;
    if (typeof value === 'string' && value.length > maxLength) {
        return <div className="whitespace-pre-wrap">{value.substring(0, maxLength)}...</div>;
    }
    return <div className="whitespace-pre-wrap">{value}</div>;
};

/**
 * Hook for managing view/edit mode state and transitions
 */
export const useViewEditMode = () => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [activeEditSection, setActiveEditSection] = useState<string | null>(null);

    const handleEditToggle = (section: string | null) => {
        if (activeEditSection === section) {
            setActiveEditSection(null);
            setIsEditMode(false);
        } else {
            setActiveEditSection(section);
            setIsEditMode(true);
        }
    };

    const handleSaveAndExit = (onSave?: () => void) => {
        if (onSave) onSave();
        setIsEditMode(false);
        setActiveEditSection(null);
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setActiveEditSection(null);
    };

    return {
        isEditMode,
        activeEditSection,
        handleEditToggle,
        handleSaveAndExit,
        handleCancelEdit
    };
};

/**
 * Props for the EditButton component
 */
interface EditButtonProps {
    onClick: () => void;
    className?: string;
}

/**
 * Consistent edit button component with icon
 */
export const EditButton: React.FC<EditButtonProps> = ({ onClick, className = "" }) => (
    <button
        onClick={onClick}
        className={`${actionButtonClass} px-3 py-1 text-sm ${className}`}
    >
        <svg className="w-4 h-4 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        Edit
    </button>
);

/**
 * Props for the SaveCancelButtons component
 */
interface SaveCancelButtonsProps {
    onSave: () => void;
    onCancel: () => void;
    className?: string;
}

/**
 * Consistent save/cancel button pair
 */
export const SaveCancelButtons: React.FC<SaveCancelButtonsProps> = ({ onSave, onCancel, className = "" }) => {
    const handleSaveClick = () => {
        onSave();
    };

    const handleCancelClick = () => {
        onCancel();
    };

    return (
        <div className={`flex space-x-3 pt-4 ${className}`}>
            <button onClick={handleSaveClick} className={`${primaryButtonClass} flex-1`}>
                Save Changes
            </button>
            <button onClick={handleCancelClick} className={`${secondaryButtonClass} flex-1`}>
                Cancel
            </button>
        </div>
    );
};

/**
 * Props for the ViewEditSection component
 */
interface ViewEditSectionProps {
    title: string;
    subtitle?: string;
    isActive: boolean;
    onEditToggle: () => void;
    children: React.ReactNode;
    className?: string;
    headerActions?: React.ReactNode;
}

/**
 * Wrapper component for consistent view/edit section layout
 */
export const ViewEditSection: React.FC<ViewEditSectionProps> = ({ 
    title, 
    subtitle, 
    isActive, 
    onEditToggle, 
    children, 
    className = "",
    headerActions
}) => (
    <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
            <div>
                <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
                {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
            </div>
            <div className="flex items-center space-x-2">
                {headerActions}
                {!isActive && <EditButton onClick={onEditToggle} />}
            </div>
        </div>
        {children}
    </div>
);

/**
 * Props for the DisplayField component
 */
interface DisplayFieldProps {
    label: string;
    value: string | number | undefined;
    className?: string;
    background?: boolean;
}

/**
 * Consistent field display component for view mode
 */
export const DisplayField: React.FC<DisplayFieldProps> = ({ 
    label, 
    value, 
    className = "", 
    background = false 
}) => (
    <div className={className}>
        <h4 className="font-medium text-gray-700 mb-1">{label}</h4>
        <div className={`text-gray-900 ${background ? 'bg-gray-50 p-3 rounded' : ''}`}>
            {formatDisplayValue(value)}
        </div>
    </div>
);

/**
 * Props for the DisplayGrid component
 */
interface DisplayGridProps {
    children: React.ReactNode;
    columns?: 1 | 2 | 3;
    className?: string;
}

/**
 * Responsive grid layout for display fields
 */
export const DisplayGrid: React.FC<DisplayGridProps> = ({ children, columns = 2, className = "" }) => (
    <div className={`grid grid-cols-1 ${columns === 2 ? 'md:grid-cols-2' : columns === 3 ? 'md:grid-cols-3' : ''} gap-4 ${className}`}>
        {children}
    </div>
);